import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import {
  Button,
  TextField as MuiTextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { Fragment, useState } from "react";
import { Helmet } from "react-helmet-async";
import MyLogoLight from "../../../vendor/ME_Light.png";
import { validatePassword } from "../form-sign-up/utils/validationFunctions";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";
import { API_BASE_URL } from "../../../config";

const TextField = styled(MuiTextField)(spacing);

const Brand = styled.img`
  fill: ${(props) => props.theme.palette.primary.main};
  display: block;
  margin: 0 auto;
  width: 256px;
  margin-bottom: 32px;
`;

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

function InsertPassword() {
  const navigate = useNavigate();
  const { key } = useParams();
  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    errorPassword: false,
    errorConfirmPassword: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const dispatchSnackbar = useDispatch();

  // Snackbar helper
  const handleSnackbar = (message, severity) => {
    dispatchSnackbar(showSnackbar({ message, severity }));
  };

  // Validate fields on change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate password
    if (name === "password") {
      setErrors((prev) => ({
        ...prev,
        errorPassword: !validatePassword(value),
        // Also revalidate confirmPassword if it has a value
        errorConfirmPassword:
          form.confirmPassword && value !== form.confirmPassword,
      }));
    }

    // Validate confirm password
    if (name === "confirmPassword") {
      setErrors((prev) => ({
        ...prev,
        errorConfirmPassword: value !== form.password,
      }));
    }
  };

  // Handle form submission
  const handleSend = async () => {
    const errorPassword = !validatePassword(form.password);
    const errorConfirmPassword = form.password !== form.confirmPassword;

    setErrors({
      errorPassword,
      errorConfirmPassword,
    });

    if (errorPassword || errorConfirmPassword) {
      handleSnackbar("Error: Controlla i campi", "error");
      return;
    } else {
      handleSnackbar("Password resettata", "success");
    }

    try {
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: {
            password: form.password,
            confermaPassword: form.confirmPassword,
            key: key,
          },
        }),
      };
      const response = await fetch(
        `${API_BASE_URL}/api/cambioPassword`,
        options
      );
      const responseData = await response.json();
      if (responseData.status !== 1) {
        handleSnackbar(responseData.message, "error");
      } else {
        handleSnackbar(responseData.message, "success");
        navigate("/auth/sign-in");
      }
    } catch (error) {
      console.error("Error during password reset:", error);
      return;
    }
  };

  return (
    <Fragment>
      <Wrapper>
        <Brand src={MyLogoLight} alt="My Logo" />
        <Helmet title="Nuova Password" />
        <Box>
          <Typography
            component="h1"
            variant="h4"
            align="center"
            gutterBottom
            mb={4}
          >
            Inserisci Nuova Password
          </Typography>
          <Typography variant="h8" gutterBottom>
            La password deve contenere:
            <ul>
              <li>minimo 8 caratteri (massimo 16)</li>
              <li>almeno una maiuscola</li>
              <li>almeno una minuscola</li>
              <li>almeno un numero</li>
              <li>
                almeno un carattere speciale tra questi: ! $ % & ( ) &lt; &gt; ,
                . ; : _
              </li>
            </ul>
          </Typography>
          <Box display="flex" flexDirection="column" gap={4} mt={3}>
            <TextField
              label="Password"
              id="password"
              name="password"
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              inputProps={{
                maxLength: 16,
                autoComplete: "new-password",
              }}
              error={errors.errorPassword}
              helperText={
                errors.errorPassword
                  ? "La password non rispetta i requisiti richiesti"
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Conferma password"
              id="confirmPassword"
              name="confirmPassword"
              fullWidth
              required
              type={showConfirm ? "text" : "password"}
              value={form.confirmPassword}
              onChange={handleChange}
              inputProps={{
                maxLength: 16,
                autoComplete: "new-password",
              }}
              error={errors.errorConfirmPassword}
              helperText={
                errors.errorConfirmPassword ? "Le password non coincidono" : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                    >
                      {showConfirm ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={
                !form.password ||
                !form.confirmPassword ||
                errors.errorPassword ||
                errors.errorConfirmPassword
              }
            >
              Resetta Password
            </Button>
          </Box>
        </Box>
      </Wrapper>
    </Fragment>
  );
}

export default InsertPassword;
